/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { environment } from "@insticator/insticator-ui"; // iui-utils
import { useDispatch, useSelector } from 'react-redux';
import { updateAdvancedConfig } from 'state/thunks/commenting';
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg'
// content
import RealTimeCapability from './realtimecapability/realtimecapability';
import GuestCommenting from './guestcommenting/guestcommenting';
// styles
import injectSheet from 'react-jss'
import styles from './advancedsection_styles'
import MessageToEndUsers from './messagetoendusers/messagetoendusers';
import LoadBehindButton from './loadbehindbutton/loadbehindbutton';
import CommentsCounter from './commentscounter/commentscounter';
import PublisherUser from './publisheruser/publisheruser';
import LoginAuthentication from './loginauthentication/loginauthentication';
import GenderOptions from './genderoptions/genderoptions';
import { setTempCommentingUnitConfig } from 'state/slices/commentingv2';
import UserProfileAbout from './userprofileabout/userprofileabout';
import StickyHeader from './stickyheader/stickyheader';


/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: ADVANCED SECTION ~~~~~~~~~~ ========== */
const AdvancedSection = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const { advancedConfig: savedAdvancedConfig, commentingUnitId, status } = useSelector(state => state.commentingV2.commentingUnitConfig)
  const { advancedConfig: tempAdvancedConfig } = useSelector(state => state.commentingV2.tempCommentingUnitConfig)

  const { productManagerConfig } = useSelector(state => state.productManager)

  const [advancedConfig, setAdvancedConfig] = useState(savedAdvancedConfig)
  const [isOpened, setIsOpened] = useState(false)
  const [isScollIntoView, setIsScrollIntoView] = useState(false)

  useEffect(() => {
    let currentLocation = window.location.href;
    const hasCommentAnchor = currentLocation.includes("?view=commentsCounter");
    if(hasCommentAnchor && (status === 'fulfilled' || status === 'rejected')) setIsOpened(true)
  },[status])

  useEffect(() => {
    if(isOpened && !isScollIntoView) scrollToComment()
  }, [isOpened, isScollIntoView])

  const fixHeaderPosition = () => {
    const header= document.getElementsByTagName('header')
    if(header.length) header[0].style.position = 'fixed'
    const pageContent = document.getElementById('content')
    pageContent.style.position = 'relative'
    pageContent.style.top = '100px'
  }

  const scrollToComment= () => {
    let currentLocation = window.location.href;
    const hasCommentAnchor = currentLocation.includes("?view=commentsCounter");
    if (hasCommentAnchor) {
      fixHeaderPosition()
      const anchorComment = document.getElementById('comments-counter');
      if(anchorComment){
        anchorComment.style.scrollMargin = '100px';
          setTimeout(() => {
            anchorComment.scrollIntoView({ behavior: "smooth",  block: 'start'  })
            setIsScrollIntoView(true)
          }, 700)
      }
    }
  }
  
  const revertHeaderContentPosition = () => {
    const header= document.getElementsByTagName('header')
    if(header.length)header[0].style.position = 'relative'
    const pageContent = document.getElementById('content')
    pageContent.style.top = 0
  }

  useEffect(() => {
    if(isScollIntoView) revertHeaderContentPosition()
  }, [isScollIntoView])


  // useEffect(() => {
  //   if(advancedConfig !== savedAdvancedConfig && !isOpened) setAdvancedConfig(savedAdvancedConfig)
  // }, [savedAdvancedConfig, isOpened, advancedConfig])

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    e.stopPropagation()
    const response = await dispatch(updateAdvancedConfig({ advancedConfig, commentingUnitId }))
    if(response.payload && !response.payload.error) setIsOpened(false)
  }, [dispatch, advancedConfig, commentingUnitId])

  const handleCancel = useCallback(async () => {
    setIsOpened(false)
    setAdvancedConfig(savedAdvancedConfig)
    dispatch(setTempCommentingUnitConfig({
      configName: 'advancedConfig',
      data: savedAdvancedConfig
    }))
  }, [dispatch, savedAdvancedConfig])

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(tempAdvancedConfig) === JSON.stringify(savedAdvancedConfig));
    return isFieldsChanged
  }, [tempAdvancedConfig, savedAdvancedConfig])

  const isValid = useMemo(() => {
    let isValid = true
    // message to end users
    if(advancedConfig.useCustomMessage && !advancedConfig.customMessageBody) isValid = false
    // guest experience
    if(advancedConfig.allowGuestCommenting && !advancedConfig.useAutoGeneratedGuestUsername && !advancedConfig.customGuestUsername) isValid = false
    // login to view comments
    if(advancedConfig.useLoadBehindButton && !advancedConfig.loadBehindButtonTitle) isValid = false
    if(advancedConfig.useLoadBehindButton && !advancedConfig.loadBehindButtonDescription) isValid = false
    if(advancedConfig.useLoadBehindButton && !advancedConfig.loadBehindButtonText) isValid = false
    return isValid
  }, [advancedConfig])


  const handleAdvancedConfigChange = useCallback((property, value) =>{
    setAdvancedConfig({
      ...advancedConfig,
      [property]: value
    })
    dispatch(setTempCommentingUnitConfig({
      configName: 'advancedConfig',
      data: {
        ...advancedConfig,
        [property]: value
      }
    }))
}, [dispatch, advancedConfig]);

  const advancedConfigData = useMemo(() => {
    return {
      realtimeCapability: {
        useRealTimeIndicator: advancedConfig.useRealTimeIndicator
      },
      guestCommenting: {
        allowGuestCommenting: advancedConfig.allowGuestCommenting,
        useAutoGeneratedGuestUsername: advancedConfig.useAutoGeneratedGuestUsername,
        customGuestUsername: advancedConfig.customGuestUsername
      },
      commentsCounter: {
        // useCommentsCounter: advancedConfig.useCommentsCounter,
        useCommentsCounter: productManagerConfig.commentsCounterEnabled,
        commentsCounterIconStyle: advancedConfig.commentsCounterIconStyle,
        commentsCounterFormat: advancedConfig.commentsCounterFormat
      },
      messageToEndUsers: {
        useCustomMessage: advancedConfig.useCustomMessage,
        customMessageBody: advancedConfig.customMessageBody
      },
      loadBehindButton: {
        useLoadBehindButton: advancedConfig.useLoadBehindButton,
        loadBehindButtonTitle: advancedConfig.loadBehindButtonTitle,
        loadBehindButtonDescription: advancedConfig.loadBehindButtonDescription,
        loadBehindButtonText: advancedConfig.loadBehindButtonText
      },
      stickyHeader: {
        useStickyHeader: advancedConfig.useStickyHeader
      }
    }
  }, [advancedConfig])

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={savedAdvancedConfig.isUpdating}
      disableSave={!isChanged ||!isValid}
      saveTitle={'Save Changes'}
      icon={<SettingsIcon />}
      title='Advanced'
    />
  );

  return (
    <ExpandableSection
      className={`expandablesectionv2 advancedsection ${props.classes.advancedsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={savedAdvancedConfig.isUpdating}
      disableSave={!isChanged}
    >
      <RealTimeCapability  onChange={handleAdvancedConfigChange} {...advancedConfigData.realtimeCapability}  />
      <GuestCommenting onChange={handleAdvancedConfigChange} {...advancedConfigData.guestCommenting}  />
      <CommentsCounter onChange={handleAdvancedConfigChange} {...advancedConfigData.commentsCounter}  />
      <StickyHeader onChange={handleAdvancedConfigChange} {...advancedConfigData.stickyHeader} />
      <MessageToEndUsers onChange={handleAdvancedConfigChange} {...advancedConfigData.messageToEndUsers} />
      <LoadBehindButton onChange={handleAdvancedConfigChange} {...advancedConfigData.loadBehindButton} />
      {environment !== 'PRODUCTION' && <PublisherUser />}
      <LoginAuthentication />
      <GenderOptions />
      <UserProfileAbout />
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AdvancedSection